/**
 * Registration-action rules for Abacus (spec 072 FR-013).
 * Pure helpers so the student click and the parent Confirm share one decision.
 */

export interface NamedCourseRef {
  id: number;
  name: string;
}

export interface CurriculumCourseRef {
  courseId: number;
  name: string;
}

export interface RegistrationCatalog {
  courses?: CurriculumCourseRef[] | null;
  abacusTiles?: {
    tiles?: { courses?: NamedCourseRef[] | null }[] | null;
  } | null;
}

/** Curriculum cards plus enrollable Abacus tile courses, keyed by course id. */
export function registrationCourseNames(
  catalog: RegistrationCatalog | null | undefined,
): Map<number, string> {
  const names = new Map<number, string>();
  for (const course of catalog?.courses ?? []) {
    names.set(course.courseId, course.name);
  }
  for (const tile of catalog?.abacusTiles?.tiles ?? []) {
    for (const course of tile.courses ?? []) {
      if (!names.has(course.id)) {
        names.set(course.id, course.name);
      }
    }
  }
  return names;
}

/** Names for the given ids, skipping ids that are not in the catalog. */
export function namesForCourseIds(
  catalog: RegistrationCatalog | null | undefined,
  courseIds: number[],
): string[] {
  const names = registrationCourseNames(catalog);
  return courseIds.flatMap((id) => {
    const name = names.get(id);
    return name ? [name] : [];
  });
}

/** Course ids the parent may keep selected after a profile refresh. */
export function selectableCourseIds(catalog: RegistrationCatalog | null | undefined): Set<number> {
  return new Set(registrationCourseNames(catalog).keys());
}

export function abacusCourseIds(catalog: RegistrationCatalog | null | undefined): Set<number> {
  const ids = new Set<number>();
  for (const tile of catalog?.abacusTiles?.tiles ?? []) {
    for (const course of tile.courses ?? []) {
      ids.add(course.id);
    }
  }
  return ids;
}

/** Opens the course only after a single successful Abacus enrollment and no failures. */
export function pathAfterAbacusEnroll(
  catalog: RegistrationCatalog | null | undefined,
  enrolledCourseIds: number[],
  failureCount: number,
  childId: number,
): string | null {
  if (failureCount > 0) {
    return null;
  }
  const abacusIds = abacusCourseIds(catalog);
  const enrolledAbacus = enrolledCourseIds.filter((id) => abacusIds.has(id));
  if (enrolledAbacus.length !== 1) {
    return null;
  }
  return `/student-courses/${enrolledAbacus[0]}?childId=${childId}`;
}

export function studentCoursePath(courseId: number): string {
  return `/student-courses/${courseId}`;
}

export function parentRegistrationPath(childId: string, courseId: number): string {
  return `/parent-register-child/${childId}?courseId=${courseId}`;
}
